import React, { useState } from 'react';

import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getMapWithProfileRoute } from 'routes/map/list';
import { hasCoordinates } from 'utils/hasCoordinates';

import { DataTable } from '@/components/data-table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PhotoCell } from './PhotoCell';
import moderationApiService, {
  useGetProfilesQuery,
  useVerifyProfileMutation,
} from 'services/moderation/moderationApiService';
import { GetProfilesResponseType } from 'services/moderation/moderationApiService.types';
import { useAppDispatch } from 'store';
import { Profile } from 'types/profile.types';

const DEFAULT_PAGE_SIZE = 20;

type VerifiedFilter = 'all' | 'unverified' | 'verified';

const VERIFIED_FILTER_OPTIONS: { value: VerifiedFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'verified', label: 'Verified' },
];

const verifiedFilterToParam = (filter: VerifiedFilter): boolean | undefined =>
  filter === 'all' ? undefined : filter === 'verified';

const Profiles = () => {
  const [verifyProfile] = useVerifyProfileMutation();
  const dispatch = useAppDispatch();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [verifiedFilter, setVerifiedFilter] = useState<VerifiedFilter>('all');

  const verifiedParam = verifiedFilterToParam(verifiedFilter);
  const queryArgs = {
    limit: pageSize,
    offset: pageIndex * pageSize,
    ...(verifiedParam !== undefined && { verified: verifiedParam }),
  };

  const { data, refetch } = useGetProfilesQuery(queryArgs);

  // No total count from the API: a full page implies there may be a next one.
  const hasNextPage = (data?.length ?? 0) === pageSize;

  const verify = (profileId: string, value: boolean) => {
    verifyProfile({
      profileId,
      value,
    }).then((r) => {
      if (r.error) {
        alert('Error');
      } else if (verifiedParam !== undefined) {
        // The profile no longer matches the filter: refetch so the page is refilled
        // from the server instead of shrinking (which would hide "next page").
        refetch();
      } else {
        dispatch(
          moderationApiService.util.updateQueryData(
            'getProfiles',
            queryArgs,
            (draft: GetProfilesResponseType) => {
              const item = draft.find((item) => item.id === profileId);
              if (item) {
                item.verified = value;
              }
            },
          ),
        );
      }
    });
  };

  const columns: ColumnDef<Profile>[] = [
    {
      header: 'Name',
      accessorKey: 'name',
    },
    {
      header: 'Birth Date',
      accessorKey: 'birthDate',
      cell: ({ row }) => {
        const birthDate = row.original.birthDate;
        const age = dayjs().diff(dayjs(birthDate), 'years');
        return (
          <div>
            {birthDate}, {age}
          </div>
        );
      },
    },
    {
      header: 'Photos',
      accessorKey: 'photos',
      cell: ({ row }) => <PhotoCell photos={row.original.photos} />,
    },
    {
      header: 'Verified',
      accessorKey: 'verified',
      cell: ({ row }) => {
        const verified = row.original.verified;
        return verified ? 'Yes' : verified === null ? '-' : 'No';
      },
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: ({ row }) => {
        const profile = row.original;
        return (
          <div className="flex gap-1">
            {hasCoordinates(profile) ? (
              <Button variant="outline" asChild>
                <Link to={getMapWithProfileRoute(profile.id)}>
                  <MapPin />
                  Show on map
                </Link>
              </Button>
            ) : (
              // No coordinates to centre the map on.
              <Button variant="outline" disabled>
                <MapPin />
                Show on map
              </Button>
            )}
            {(profile.verified === false || profile.verified === null) && (
              <Button onClick={() => verify(profile.id, true)}>Approve</Button>
            )}
            {(profile.verified || profile.verified === null) && (
              <Button variant="destructive" onClick={() => verify(profile.id, false)}>
                Reject
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Verification</span>
        <Select
          value={verifiedFilter}
          onValueChange={(value) => {
            setVerifiedFilter(value as VerifiedFilter);
            setPageIndex(0);
          }}>
          <SelectTrigger className="w-[140px]" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VERIFIED_FILTER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DataTable
        columns={columns}
        data={data ?? []}
        rowClassName="h-20"
        pagination={{
          pageIndex,
          pageSize,
          hasNextPage,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />
    </div>
  );
};

export default Profiles;
